// SubBrokerV2 — batch 3's rebuild of Sub-broker trade (UI rebuild; drawn when lib/ui-v2.ts switches batch 3 on).
//
// ASTRA'S SHAPE (operator's ruling, 26 Oct 2026: "simplified or closer to that version"): one card in two groups —
// Desk agreement (the sub-broker, which way his client trades, the coin and currency, the desk rate and the desk total)
// and Client settlement (what the client pays and receives, the client rate, the margin as a result, and the two
// accounts the money went through) — the optional client name, the date and notes under More, then "Review three
// entries", which opens the three-entry readback (beside the card from lg, inside it on the phone) and turns the
// primary into the booking press.
//
// THE STATE MACHINE IS SubBrokerTrade's, COPIED VERBATIM (batch 3 brief): the three keys minted once and held across a
// retry, landedRef and what a retry skips, the repeat guard and which entry it was asking for, the lock once the trade
// landed or may have, the date re-mint rule, readState on the party and the accounts, the calculators and their sync,
// the INR movement defaults, the wallet deciding the chain, and the posted readback. The pure helper is imported
// (deriveSubBroker). Only what is shown, in what order and grouped how is new — and one UI flag, `reviewing`, which
// decides whether the primary asks for the review or books; it never posts and never blocks a retry.
import { useState, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { tg } from '../../lib/telegram';
import {
  ASSETS, isFiat, kindsForCategory, treasuryPath, partyPath, makeReference, DEFAULT_RAILS,
} from '../../lib/workflow';
import { amountProblem, rateProblem } from '../../lib/amount';
import { errorWords, isSentinel, nothingLanded, readWords } from '../../lib/errors';
import { Field } from '../../components/Field';
import { ErrorLine } from '../../components/ErrorLine';
import { AccountsDown } from '../../components/kit';
import { CopyRef } from '../../components/CopyRef';
import { BalanceRows } from '../../components/v2/record/BalanceRows';
import { MoreDetails } from '../../components/MoreDetails';
import { RailPicker, type RailValue } from '../../components/RailPicker';
import { RepeatPanel, useRepeatGuard } from '../../components/RepeatPanel';
import { AsOfChips } from '../../components/AsOfChips';
import { useTradeMath } from '../../hooks/useTradeMath';
import { partyName, foldAsset, sameAsset, partyBalanceOf } from '../../lib/naming';
import { readState } from '../../lib/read-state';
import {
  displayAsset, fmtAmount, fmtDate, fmtDay, fmtTime, dubaiDate,
  dubaiWallToIso, backdateProblem, shownAmount,
} from '../../lib/format';
import { deriveSubBroker, type ClientSide } from '../SubBrokerTrade';
import { PageHead, Notice, Fact, StateLine, ReadFailed, Pending, ReadNotice, TAP } from '../../components/v2/kit';
import { FormCard, FormGroup, FormPage, BeforeConfirming, railWord } from '../../components/v2/broker/form';
import { AboutFold } from '../../components/v2/record/parts';
import { Receipt, ReceiptAmount, postedAsOf } from '../../components/v2/Receipt';

type Desk = 'dubaiotc' | 'indiaotc';
const isDesk = (v: unknown): v is Desk => v === 'dubaiotc' || v === 'indiaotc';

/** The three entries, in posting order. */
type Step = 'trade' | 'money' | 'delivery';

/** The registry's coins and currencies — the same list the trade screen offers. */
const COINS: readonly string[] = ASSETS.filter((a) => !isFiat(a));
const FIATS: readonly string[] = ASSETS.filter((a) => isFiat(a));

/** The chain matters for the default wallet: the TRC hot wallet holds TRC
 *  coins only, so the exact code decides, not the fold. */
const isTrc = (coin: string) => coin === DEFAULT_RAILS.hotTrc.asset;   // dialect-ok: chain identity — the TRC hot wallet is the default for USDT·TRC alone, never for USDT·ERC

/** A figure and its unit as ONE value (a no-break space joins them): a line wraps between values, never inside one. */
const money = (a: number, asset: string, places?: number) => `${fmtAmount(a, asset, places)} ${displayAsset(asset)}`;

/** What has landed, by step — the record a retry reads before it posts (SubBrokerTrade's, verbatim). */
interface Landed {
  trade?:    { dealId: string; deduped: boolean };
  money?:    { reference: string; deduped: boolean; prior?: boolean };
  delivery?: { reference: string; deduped: boolean; prior?: boolean };
}

const COUNT = ['', 'one', 'two', 'three'] as const;

export function SubBrokerV2() {
  const { slug } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const entity: Desk = isDesk(search.get('entity')) ? (search.get('entity') as Desk) : 'dubaiotc';
  const nav = useNavigate();

  // Same key as the party page, so arriving from it is a cache hit — and
  // refreshMoneyViews refetches it after every posting, which is how the
  // success line reads the LIVE box (see `posted.at`).
  const q = useQuery({
    queryKey: ['party', slug, entity],
    queryFn:  () => api.partyDetail(slug!, entity),
    enabled:  !!slug,
  });
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  // What each read is worth — `readState` (lib/read-state.ts; Appendix B): 'known' only once a read SUCCEEDED, 'waiting' while nothing has
  // answered (a first read, the retry of a failure, an offline pause), 'failed' on an error — whatever the cache holds.
  const partyRead = readState(q);
  const accounts = readState(treasury);

  const [side, setSide]             = useState<ClientSide>(search.get('dir') === 'sell' ? 'sell' : 'buy');
  const [coin, setCoin]             = useState<string>(DEFAULT_RAILS.hotTrc.asset);
  const [fiat, setFiat]             = useState<string>(DEFAULT_RAILS.cash.asset);
  // TWO CALCULATORS, ONE PER SIDE, SHARING THE QUANTITY. Each holds quantity
  // (coin), rate (fiat per coin, the pair's way round) and total (fiat); any
  // two fill the third on every keystroke. The desk's is the trade with the
  // desk; the client's is what his client paid or gets.
  const desk   = useTradeMath(coin, fiat);
  const client = useTradeMath(coin, fiat);
  // ONE QUANTITY BOX WRITES TO BOTH. When a side DERIVES the quantity instead
  // (desk total and desk rate pasted, no quantity typed) the other side must
  // hear it, or its total would never fill. The deriving side is the source
  // and the quantity lands on the other as if typed; when both sides hold it
  // as a fact they already agree. Runs during render — the hook's own way —
  // so the next paint shows both sides in step.
  const setQty = (v: string) => { desk.setAmount(coin, v); client.setAmount(coin, v); };
  const qty = desk.amountOf(coin);
  if (qty !== client.amountOf(coin)) {
    if (desk.derived === 'base')        client.preset({ asset: coin }, qty);
    else if (client.derived === 'base') desk.preset({ asset: coin }, client.amountOf(coin));
  }
  // ?qty= ?desk= ?client= count as typed first, quantity before rate, so a
  // rate typed afterwards moves the total and the quantity anchors. Once, on
  // the first render, before the first paint.
  const prefilled = useRef(false);
  if (!prefilled.current) {
    prefilled.current = true;
    const qtyParam = search.get('qty') ?? '', deskParam = search.get('desk') ?? '', clientParam = search.get('client') ?? '';
    if (qtyParam)    { desk.preset({ asset: coin }, qtyParam); client.preset({ asset: coin }, qtyParam); }
    if (deskParam)   desk.preset('rate', deskParam);
    if (clientParam) client.preset('rate', clientParam);
  }
  const [clientName, setClientName] = useState('');
  const [notes, setNotes]           = useState('');
  // Which figure box has the cursor — a box the calculator filled shows its figure grouped, four places at most
  // (shownAmount, display only); the one being edited shows the raw string, so a formatter never fights the typing.
  const [editing, setEditing] = useState<'qty' | 'deskTotal' | 'clientTotal' | null>(null);
  const [when, setWhen]             = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);
  // Both movements default ON — the operator's own example has the client's
  // money and the coins both moved on the day. Untick the one that has not.
  //
  // EXCEPT THE CURRENCY LEG WHEN THE CURRENCY IS INR (sweep item 36, 14 Sept
  // 2026): angadiya money lands days later, so on the day of the trade the
  // client's INR (a client-buy) or the INR out to him (a client-sell) has
  // usually not moved yet — it starts unticked and is recorded from his page
  // when it lands. Re-applied when the currency chip or the side changes;
  // after that the ticks are the operator's.
  const [moneyOn, setMoneyOn]       = useState(true);
  const [deliveryOn, setDeliveryOn] = useState(true);
  const applyMovementDefaults = (s: ClientSide, f: string) => {
    const lags = sameAsset(f, 'INR');
    setMoneyOn(!(lags && s === 'buy'));
    setDeliveryOn(!(lags && s === 'sell'));
  };
  const pickSide = (s: ClientSide) => { setSide(s); applyMovementDefaults(s, fiat); };
  // The fiat rail serves the fiat movement (in on a client-buy, out on a
  // client-sell); the wallet serves the coin movement (the other way round).
  const [fiatRail, setFiatRail] = useState<RailValue>({ ...DEFAULT_RAILS.cash });
  const [wallet, setWallet]     = useState<RailValue>({ ...DEFAULT_RAILS.hotTrc });

  // A chip change re-defaults its rail: the office cash rail holds AED, the
  // TRC hot wallet holds USDT·TRC, and neither is a sane default for anything
  // else — a blank picker asks the question instead of answering it wrongly.
  const pickFiat = (f: string) => {
    setFiat(f);
    setFiatRail(sameAsset(f, DEFAULT_RAILS.cash.asset) ? { ...DEFAULT_RAILS.cash } : { slug: '', asset: '' });
    applyMovementDefaults(side, f);
  };
  const pickCoin = (c: string) => {
    setCoin(c);
    setWallet(isTrc(c) ? { ...DEFAULT_RAILS.hotTrc } : { slug: '', asset: '' });
  };
  // THE WALLET DECIDES THE CHAIN (RecordMoney's rule). A wallet row speaks the
  // chain code (USDTERC) or the fold (USDT); when it names a chain that is the
  // same coin as the chip but a different chain, the chip follows — the coins
  // that leave the ERC wallet are ERC coins, and the trade's leg must say so.
  // A different coin altogether is a mismatch, flagged below, never adopted.
  const pickWallet = (v: RailValue) => {
    setWallet(v);
    const chain = COINS.find((a) => a === v.asset && sameAsset(a, coin));   // dialect-ok: exact chain code, guarded by the fold on the same line
    if (chain) setCoin(chain);
  };
  const walletMismatch   = !!wallet.asset   && !sameAsset(wallet.asset, coin);
  const fiatRailMismatch = !!fiatRail.asset && !sameAsset(fiatRail.asset, fiat);

  // Everything the plan needs is read from the calculators: the quantity, the
  // two rates (posted as the trade's rate and in its notes) and the two
  // totals (posted as the legs and the movements).
  const deskTotal   = desk.amountOf(fiat);
  const clientTotal = client.amountOf(fiat);
  // WHICH BOX MOVES, so the operator sees it at a glance: "auto" on the label
  // and muted figures (operator, 12 Sept 2026). Each side's calculator says
  // which of its three it fills. The quantity box is shared by both, so it
  // reads "auto" only when BOTH sides derive it — a quantity one side derives
  // lands on the other as a fact (the sync above), and a fact on either side
  // is a fact in the shared box.
  const qtyAuto = desk.isDerived(coin) && client.isDerived(coin);
  const qtyN         = Number(qty) || 0;
  const deskN        = Number(desk.rate) || 0;
  const clientN      = Number(client.rate) || 0;
  const deskFiatN    = Number(deskTotal) || 0;
  const clientFiatN  = Number(clientTotal) || 0;
  const qtyWords         = amountProblem(qty);
  const deskWords        = rateProblem(desk.rate);
  const clientWords      = rateProblem(client.rate);
  const deskTotalWords   = amountProblem(deskTotal);
  const clientTotalWords = amountProblem(clientTotal);
  const plan = useMemo(() => {
    if (qtyN <= 0 || deskN <= 0 || clientN <= 0 || deskFiatN <= 0 || clientFiatN <= 0
      || qtyWords || deskWords || clientWords || deskTotalWords || clientTotalWords) return null;
    return deriveSubBroker({ side, coin, fiat, qty: qtyN, deskFiat: deskFiatN, clientFiat: clientFiatN });
  }, [side, coin, fiat, qtyN, deskN, clientN, deskFiatN, clientFiatN, qtyWords, deskWords, clientWords, deskTotalWords, clientTotalWords]);

  // Which rail each movement goes through, this side round.
  const moneyRail    = side === 'buy' ? fiatRail : wallet;
  const deliveryRail = side === 'buy' ? wallet   : fiatRail;
  const moneyMismatch    = side === 'buy' ? fiatRailMismatch : walletMismatch;
  const deliveryMismatch = side === 'buy' ? walletMismatch   : fiatRailMismatch;
  const moneyNoun    = side === 'buy' ? "the client's money" : "the client's coins";
  const deliveryNoun = side === 'buy' ? 'the coins' : 'the money';

  // ── submit ───────────────────────────────────────────────────────────────
  // THREE KEYS, EACH HELD ACROSS A RETRY. The deal id and the two references
  // are minted once and kept in refs (NewTrade, RecordMoney); `landedRef` is
  // the record of what the books have answered, read synchronously by the
  // next attempt so it skips what already stands. `landed` mirrors it for
  // the screen.
  const dealIdRef  = useRef<string | null>(null);
  const rcptRef    = useRef<string | null>(null);
  const payRef     = useRef<string | null>(null);
  const landedRef  = useRef<Landed>({});
  const [landed, setLanded] = useState<Landed>({});
  // ASTRA'S REVIEW STEP (UI only): "Review three entries" opens the readback; the next press books. Never posts, never
  // gates a retry — once the trade has landed or may have, the press books what remains as before.
  const [reviewing, setReviewing] = useState(false);
  // The repeat guard is shared with every posting screen. Each of the three
  // entries can meet it, so the screen remembers WHICH one was wrapped when a
  // twin came back (`asking`), and "post it again anyway" confirms that one.
  const { twin, clearTwin, guard } = useRepeatGuard();
  const askingRef    = useRef<Step>('trade');
  const confirmedRef = useRef<Set<Step>>(new Set());

  const [posted, setPosted] = useState<{
    at: number; asOf: string | null; name: string; side: ClientSide;
    dealId: string; tradeDeduped: boolean;
    money: Landed['money'] | null; delivery: Landed['delivery'] | null;
    fiat: string; coin: string; margin: number;
    /** Projected boxes, from the balances the plan was derived on; the
     *  success line prefers the LIVE figures once the party is re-read. */
    fiatAfter: number; coinAfter: number;
  } | null>(null);

  const post = async () => {
    if (!plan || !slug || !q.data) throw new Error('nothing to book');
    const name  = partyName(q.data.party);
    const party = partyPath(slug, q.data.party.party_type, entity);
    const who   = clientName.trim() || '—';
    const L = landedRef.current;

    // (1) THE TRADE, at the desk rate, settle-later.
    if (!L.trade) {
      // The id carries the day the trade HAPPENED, like NewTrade's.
      dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
      const dealId = dealIdRef.current;
      askingRef.current = 'trade';
      const data = await guard(() => api.bookTrade({
        entity,
        direction:  plan.direction,
        party_slug: slug,
        // Nothing moves in this entry — the two movements below are what
        // moved, and the sweep matches them to these legs.
        unsettled:   true,
        amount_take: plan.take,
        amount_give: plan.give,
        // The desk rate as it stands in its box — typed, or re-derived from
        // a corrected desk total.
        rate:        desk.rate.trim(),
        deal_id:     dealId,
        notes: [
          `sub-broker · client ${who} @ ${client.rate.trim()} ${displayAsset(fiat)} per ${displayAsset(coin)}`,
          notes.trim(),
        ].filter(Boolean).join(' · '),
        effective_at:   whenIso || undefined,
        confirm_repeat: confirmedRef.current.has('trade') || undefined,
      }));
      L.trade = { dealId: data.deal_id ?? dealId, deduped: !!data.deduped };
      setLanded({ ...L });
    }
    const dealId = L.trade.dealId;

    // (2) THE CLIENT'S MONEY, in: party → rail.
    if (moneyOn && !L.money) {
      rcptRef.current ??= makeReference('RCPT', whenIso || undefined);
      const reference = rcptRef.current;
      askingRef.current = 'money';
      const data = await guard(() => api.transfer({
        source:      party,
        destination: treasuryPath(moneyRail.slug, plan.moneyIn.asset, entity),
        amount:      plan.moneyIn,
        fee_payer:   'house',
        reference,
        notes:       `from ${who} for ${name} · sub-broker trade ${dealId}`,
        // Links this leg back to (1)'s deal, so Home's journal can fold it
        // with the delivery leg below into one row instead of two — the
        // notes sentence above already said "sub-broker trade {dealId}" in
        // words; this is the same fact as a field a screen can read without
        // parsing a sentence. `linked_deal_id`, never `deal_id`: this leg is
        // an ordinary transfer, not the deal's own leg, and `deal_id` here
        // would make the Reverse door refuse it while trade-void never
        // reverses a transfer either (Mechanisms, transfers.ts, 30 Sept –
        // 1 Oct 2026).
        linked_deal_id: dealId,
        effective_at:   whenIso || undefined,
        confirm_repeat: confirmedRef.current.has('money') || undefined,
      }));
      L.money = { reference: data.reference ?? reference, deduped: !!data.deduped };
      setLanded({ ...L });
    }

    // (3) THE DELIVERY, out: rail → party.
    if (deliveryOn && !L.delivery) {
      payRef.current ??= makeReference('PAY', whenIso || undefined);
      const reference = payRef.current;
      askingRef.current = 'delivery';
      const data = await guard(() => api.transfer({
        source:      treasuryPath(deliveryRail.slug, plan.moneyOut.asset, entity),
        destination: party,
        amount:      plan.moneyOut,
        fee_payer:   'house',
        reference,
        notes:       `to ${who} for ${name} · sub-broker trade ${dealId}`,
        linked_deal_id: dealId,
        effective_at:   whenIso || undefined,
        confirm_repeat: confirmedRef.current.has('delivery') || undefined,
      }));
      L.delivery = { reference: data.reference ?? reference, deduped: !!data.deduped };
      setLanded({ ...L });
    }

    // WHAT THE BOX WILL READ, projected from the balances in hand. Sign law:
    // what he pays (take, or money out to him) lifts his box, what he
    // receives (give, or money in from him) lowers it.
    // A movement answered "It's already booked" was posted before this entry
    // set, so the balances in hand already carry it — projecting it again
    // would count it twice (review, 12 Sept 2026).
    const bal = q.data.balances;
    const fiatNow = bal[foldAsset(fiat)] ?? bal[fiat] ?? 0;
    const coinNow = bal[foldAsset(coin)] ?? bal[coin] ?? 0;
    const moneyNew    = !!L.money    && !L.money.prior;
    const deliveryNew = !!L.delivery && !L.delivery.prior;
    const fiatAfter = side === 'buy'
      ? fiatNow + plan.deskFiat - (moneyNew ? plan.clientFiat : 0)
      : fiatNow - plan.deskFiat + (deliveryNew ? plan.clientFiat : 0);
    const coinAfter = side === 'buy'
      ? coinNow - qtyN + (deliveryNew ? qtyN : 0)
      : coinNow + qtyN - (moneyNew ? qtyN : 0);
    return {
      at: Date.now(), asOf: whenIso || null, name, side,
      dealId, tradeDeduped: L.trade.deduped,
      money: L.money ?? null, delivery: L.delivery ?? null,
      fiat, coin, margin: plan.margin, fiatAfter, coinAfter,
    };
  };

  const submit = useMutation({
    mutationFn: post,
    onSuccess: (r) => {
      setPosted(r);
      // Every key leaves with the entry set it was minted for.
      landedRef.current = {}; setLanded({});
      dealIdRef.current = null; rcptRef.current = null; payRef.current = null;
      confirmedRef.current.clear(); clearTwin();
      // A clean slate for the next one. The desk rate, the coin, the currency
      // and the rails stay — the next of his trades that day shares them. The
      // rate is re-seated as the desk side's one fact, so it stays whether it
      // was typed or re-derived from a corrected total; the client side
      // starts empty.
      const deskRate = desk.rate;
      desk.reset(); client.reset();
      if (deskRate) desk.preset('rate', deskRate);
      setClientName(''); setNotes(''); setWhen('');
      setReviewing(false);
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  // Once the trade has landed the figures are locked to it: a changed
  // quantity or rate would make the movements disagree with the legs they are
  // meant to close. The rails and the two ticks stay live, so a wrong rail
  // can be corrected and the remaining entries retried.
  //
  // THE SAME LOCK WHEN THE TRADE MAY HAVE LANDED. A 5xx, a dropped connection
  // or a lost answer on the trade POST keeps the deal id (lib/errors.ts
  // nothingLanded), so the retry dedupes — to the figures FIRST sent. Left
  // editable, a changed quantity or rate would have the trade stand at the
  // old figures while the money and the delivery post at the new ones
  // (review, 12 Sept 2026). So the figures lock the moment a failure is not
  // provably answered before the first ledger write, and only "start another"
  // — a decision, not a keystroke — lets them go.
  const mayHold = submit.isError && !nothingLanded(submit.error);
  const locked  = !!landed.trade || mayHold;
  const busy    = submit.isPending;

  // THE KEYS CARRY THE DAY, so a change of date re-mints them — unless the
  // books may already hold one: a posting in flight, or a last failure not
  // provably answered before the first ledger write (NewTrade's rule,
  // lib/errors.ts nothingLanded). The field is disabled once the trade has
  // landed or may have, so the two references can never drift from the
  // deal's day; the check here is the belt to that brace.
  const onWhenChange = (v: string) => {
    setWhen(v);
    if (!submit.isPending && !mayHold && !landedRef.current.trade) {
      dealIdRef.current = null; rcptRef.current = null; payRef.current = null;
      confirmedRef.current.clear(); clearTwin();
    }
  };

  // Everything from this attempt is dropped; what the books hold stands, and
  // the message above the button has already said what to record by hand.
  const startAnother = () => {
    landedRef.current = {}; setLanded({});
    dealIdRef.current = null; rcptRef.current = null; payRef.current = null;
    confirmedRef.current.clear(); clearTwin(); submit.reset();
  };

  // A READ THAT FAILED IS NOT AN EMPTY ONE — said in one line, with the way out and a way back.
  const retry = (again: () => unknown) => <button type="button" className="btn-link text-meta" onClick={() => void again()}>Retry</button>;
  const head = <PageHead title="Sub-broker trade" />;
  // THE SCREEN IS REPLACED ONLY WHEN THERE IS NOTHING TO SHOW (ConvertBalance's rule): with the party cached the form
  // and the answer stay, and the line above the press says the failure once, beside them.
  if (partyRead === 'failed' && !q.data) {
    return (
      <>
        {head}
        <ReadNotice what="this party" error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  }
  if (!q.data) return <>{head}<Pending /></>;

  const name = partyName(q.data.party);
  const partyHref = `/parties/${encodeURIComponent(q.data.party.slug)}${entity === 'dubaiotc' ? '' : `?entity=${entity}`}`;
  // THE RUNNING ACCOUNT decides whether his trade closes by itself (About this job says which case he is).
  const rides = !!q.data.party.trade_remainder_rides_on_account;
  // MERCHANTS ONLY — the party page shows the door to a counterparty alone, but a pasted URL reaches here for anyone.
  if (q.data.party.party_type !== 'counterparty') {
    return (
      <>
        {head}
        <Notice tone="warn" action={<Link to={partyHref} className="btn-link text-body">Party page →</Link>}>
          {name} is not a merchant.
        </Notice>
      </>
    );
  }


  // A REFRESH THAT FAILED keeps the last good read beside the error (TanStack v5): the form may stay on screen, but nothing is CLAIMED
  // from that read and nothing is posted off it — design system Appendix B. This is the one place the party's read becomes "unknown"
  // (`readState`, not 'known'): the box line of the answer and every post door below follow it, and Try again lifts it. (A first read
  // that has not answered never gets here: the guards above return.)
  const unknown = partyRead !== 'known';
  const rows = treasury.data?.rows ?? [];
  // No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): an open picker would say "No such account" and point at New party.
  const accountsDown = accounts === 'failed' && !treasury.data;
  const live = posted && q.dataUpdatedAt > posted.at;
  const fiatAfter = posted ? (live ? partyBalanceOf(q.data.balances, posted.fiat) : posted.fiatAfter) : 0;
  const coinAfter = posted ? (live ? partyBalanceOf(q.data.balances, posted.coin) : posted.coinAfter) : 0;

  // THE BUTTON NAMES THE ENTRIES (sweep item 36): "Book · trade + money in +
  // coins out" on a client-buy with both movements ticked; each unticked
  // movement drops out of the words, so the label is the list of what posts.
  const entryWords = [
    'trade',
    moneyOn    ? (side === 'buy' ? 'money in'  : 'coins in')  : null,
    deliveryOn ? (side === 'buy' ? 'coins out' : 'money out') : null,
  ].filter(Boolean).join(' + ');
  const remaining = (landed.trade ? 0 : 1) + (moneyOn && !landed.money ? 1 : 0) + (deliveryOn && !landed.delivery ? 1 : 0);
  const ready = !busy && !!plan && !whenWords
    && (!moneyOn    || (!!moneyRail.slug    && !moneyMismatch))
    && (!deliveryOn || (!!deliveryRail.slug && !deliveryMismatch));
  // The figures and the accounts are in order but the party could not be re-read: nothing is posted off a box nobody has read
  // since the last posting (the retry of a part-way set included) — Try again first.
  const canPost = ready && !unknown;
  // EVERY DOOR THAT POSTS asks the same question in its own CALLBACK, not only in its button's state — a disabled button is a label,
  // not a lock, and the repeat panel's doors are callbacks a screen hands away. Nothing is posted off a party nobody could re-read.
  const send = (needsReady = false) => { if (unknown || (needsReady && !ready)) return; submit.mutate(); };
  const coinName = displayAsset(coin);
  // THE TOGGLE NAMES THE COIN BY ITS FOLD (§6 trade direction, review
  // 14 Sept 2026): "Buys USDT / Sells USDT" on both trade screens — New trade
  // folds the chain off the base too. The chip, the Quantity hint and the
  // readback keep the exact chain code (coinName); only the toggle folds.
  const coinWord = displayAsset(foldAsset(coin));

  // ── THE V2 DRAWING ───────────────────────────────────────────────────────────────────────────────────────────────
  const entries = 1 + (moneyOn ? 1 : 0) + (deliveryOn ? 1 : 0);
  const entriesWord = `${COUNT[entries]} ${entries === 1 ? 'entry' : 'entries'}`;
  // The press books once the review is open — or at once when the trade has landed or may have (a retry of what remains
  // is never sent back through the review).
  const confirming = reviewing || locked;
  // THE FOOT SAYS WHAT IS STILL MISSING (Astra: "identify the missing prerequisite") — the first one, in a few words.
  const missing = unknown ? 'Party not read — try again above.'
    : !plan ? 'Enter the amounts and both rates.'
    : whenWords ? whenWords
    : moneyOn && !moneyRail.slug ? 'Pick where the client paid in.'
    : moneyOn && moneyMismatch ? 'Check the pay-in account.'
    : deliveryOn && !deliveryRail.slug ? `Pick where the ${side === 'buy' ? 'coins' : 'money'} went from.`
    : deliveryOn && deliveryMismatch ? 'Check the pay-out account.'
    : null;
  const disabled = locked || busy;
  // The client's two legs, this side round: a client-buy pays the currency at the client total and receives the coins;
  // a client-sell pays the coins and receives the currency.
  const qtyBox = (
    <input className={`input${qtyAuto ? ' text-tg-hint' : ''}`} value={shownAmount(qty, coin, qtyAuto, editing === 'qty')} onChange={(e) => setQty(e.target.value)} onFocus={() => setEditing('qty')} onBlur={() => setEditing(null)} inputMode="decimal" disabled={disabled} aria-label="Quantity" />
  );
  const clientTotalBox = (
    <input className={`input${client.isDerived(fiat) ? ' text-tg-hint' : ''}`} value={shownAmount(clientTotal, fiat, client.isDerived(fiat), editing === 'clientTotal')} onChange={(e) => client.setAmount(fiat, e.target.value)} onFocus={() => setEditing('clientTotal')} onBlur={() => setEditing(null)} inputMode="decimal" disabled={disabled} aria-label="Client total" />
  );
  const qtyHint = qtyAuto ? `${coinName} · auto` : coinName;
  const clientTotalHint = client.isDerived(fiat) ? `${displayAsset(fiat)} · auto` : displayAsset(fiat);
  const fiatPlaces = (a: string) => (isFiat(a) ? 2 : undefined);

  // THE THREE-ENTRY REVIEW — the readback before the one press that books (Astra: "Review three entries").
  const review = confirming && plan && !landed.trade ? (
    <div>
      <p className="mb-1 text-meta text-tg-hint">{entryWords}</p>
      <Fact label="Trade">
        {name} {side === 'buy' ? 'pays' : 'receives'} <span className="whitespace-nowrap">{money(plan.deskFiat, fiat, 2)}</span>,
        {' '}{side === 'buy' ? 'receives' : 'pays'} <span className="whitespace-nowrap">{money(qtyN, coin)}</span> @ {desk.rate.trim()}
      </Fact>
      <Fact label={side === 'buy' ? 'Money in' : 'Coins in'}>
        {moneyOn
          ? <><span className="whitespace-nowrap">{money(plan.moneyIn.amount, plan.moneyIn.asset, fiatPlaces(plan.moneyIn.asset))}</span> · {railWord(rows, moneyRail) || '—'}</>
          : <span className="text-tg-hint">Later, from his page</span>}
      </Fact>
      <Fact label={side === 'buy' ? 'Coins out' : 'Money out'}>
        {deliveryOn
          ? <><span className="whitespace-nowrap">{money(plan.moneyOut.amount, plan.moneyOut.asset, fiatPlaces(plan.moneyOut.asset))}</span> · {railWord(rows, deliveryRail) || '—'}</>
          : <span className="text-tg-hint">Later, from his page</span>}
      </Fact>
      <Fact label="Margin">
        <span className={plan.margin < 0 ? 'text-warn' : ''}>
          <span className="whitespace-nowrap">{money(plan.margin, fiat, 2)}</span> · on his account{plan.margin < 0 && ' · below the desk rate'}
        </span>
      </Fact>
      {whenIso && !whenWords && <Fact label="As of">{fmtDate(whenIso)}</Fact>}
    </div>
  ) : undefined;

  // EVERY ANSWER OPENS ABOVE THE PRESS (§7 rule 11) — SubBrokerTrade's lines, as they were.
  const answers = (
    <>
      {/* A refresh that FAILED while the party is in hand: the form and the outcome stay; this says it once. */}
      {unknown && <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} />}

      {/* WHAT STANDS SO FAR — from the first entry that lands until all have, so a refusal on the second or third entry
          is never read as "nothing was booked". */}
      {landed.trade && (
        <div className="rounded-lg border border-stroke-soft bg-panel-2 px-3 py-2 text-meta max-lg:text-body space-y-0.5">
          <p className="text-tg-text">
            <span className="text-pos">✓</span> Trade booked as <span className="font-mono">{landed.trade.dealId}</span>
            {landed.trade.deduped && <span className="text-tg-hint"> (already in the books — kept)</span>}
          </p>
          <p className={landed.money ? 'text-tg-text' : 'text-tg-hint'}>
            {landed.money
              ? <><span className="text-pos">✓</span> {moneyNoun} recorded as <span className="font-mono">{landed.money.reference}</span></>
              : moneyOn ? <>— {moneyNoun}: not recorded yet</> : <>— {moneyNoun}: left for the party page</>}
          </p>
          <p className={landed.delivery ? 'text-tg-text' : 'text-tg-hint'}>
            {landed.delivery
              ? <><span className="text-pos">✓</span> {deliveryNoun} recorded as <span className="font-mono">{landed.delivery.reference}</span></>
              : deliveryOn ? <>— {deliveryNoun}: not recorded yet</> : <>— {deliveryNoun}: left for the party page</>}
          </p>
          <p className="text-tg-hint pt-1">Figures are locked to the booked trade. <button type="button" className="btn-link text-meta" onClick={startAnother} disabled={busy}>Start another instead</button></p>
        </div>
      )}

      {/* A REFUSAL AFTER THE TRADE LANDED says what stands and what does not; before it, "Nothing was booked" only when
          the refusal provably came before the first ledger write — otherwise the trade MAY stand under its id. */}
      {submit.isError && !isSentinel(submit.error) && (
        landed.trade ? (
          <div className="rounded-lg bg-warn-bg p-3 text-body space-y-1">
            <StateLine
              tone="warn"
              more="Record it from the party page, or retry the remaining entries below."
              action={<Link to={partyHref} className="btn-link text-meta">Party page →</Link>}
            >
              Trade booked — {!landed.money && moneyOn ? (side === 'buy' ? 'money in' : 'coins in') : (side === 'buy' ? 'coins out' : 'money out')} NOT recorded.
            </StateLine>
            <p className="text-meta text-tg-hint">{errorWords(submit.error)}</p>
          </div>
        ) : nothingLanded(submit.error) ? (
          <ErrorLine className="mt-0 text-body">{errorWords(submit.error)} Nothing was booked.</ErrorLine>
        ) : (
          <div className="space-y-1">
            <ErrorLine className="mt-0 text-body">{errorWords(submit.error)}</ErrorLine>
            <StateLine
              tone="warn"
              more={<>
                The figures are locked to the booked trade: the books may hold it
                {dealIdRef.current && <> as <span className="font-mono">{dealIdRef.current}</span></>} with the answer lost, so a retry
                keeps that id and these figures and cannot book it twice. Check Trades if in doubt.
              </>}
              action={<>
                <Link to="/trades" className="btn-link text-meta">Open Trades →</Link>
                <button type="button" className="btn-link text-meta" onClick={startAnother} disabled={busy}>Start another instead</button>
              </>}
            >
              Trade may be booked — a retry can&rsquo;t book it twice.
            </StateLine>
          </div>
        )
      )}

      {/* The receipt: the ids, the day, the box in BoxLines' words — live once the party is re-read, projected until then. */}
      {twin && (
        <RepeatPanel
          twin={twin}
          pending={busy || !!whenWords || unknown}
          // A twin TRADE already booked: this whole entry set was done earlier — leave. A twin MOVEMENT already booked
          // stands in for that entry: mark it and carry on with whatever is still to post.
          onAlreadyBooked={() => {
            const step = askingRef.current;
            clearTwin();
            if (step === 'trade') { nav(-1); return; }
            landedRef.current[step] = { reference: twin.reference ?? 'an earlier entry', deduped: true, prior: true };
            setLanded({ ...landedRef.current });
            send();
          }}
          onPostAnyway={() => { if (unknown) return; confirmedRef.current.add(askingRef.current); clearTwin(); send(); }}
        />
      )}
    </>
  );

  const primary = (
    <button
      className="btn-primary w-full lg:w-auto lg:px-6"
      disabled={!canPost}
      onClick={() => { if (confirming) send(true); else if (canPost) setReviewing(true); }}
    >
      {busy ? 'Booking…'
        // The figures and the accounts are in order; only the party's read stands between the operator and the button.
        : ready && unknown ? 'Party not read'
        : landed.trade
          ? (remaining === 0 ? 'Finish' : `Retry the remaining ${remaining === 1 ? 'entry' : 'entries'}`)
          : confirming
            ? `Book ${entriesWord}${whenIso && !whenWords ? ` · as of ${fmtDay(whenIso)}` : ''}`
            : `Review ${entriesWord} →`}
    </button>
  );

  const card = (
    <FormCard review={review} answers={answers} primary={primary} note={missing && !busy && !landed.trade ? missing : null}>
      <FormGroup title="Desk agreement">
        {/* Read-only context is compact text: the screen lives under his party page. */}
        <Field label="Sub-broker" className="max-lg:col-span-2">
          <p className="truncate py-2 text-body lg:py-2.5" title={name}>{name}</p>
        </Field>
        {/* THE CLIENT'S SIDE, not the sub-broker's — "he sold 10k to his client"; the desk's direction follows. The
            toggle names the coin by its fold (§6), as Book trade's does. */}
        <Field label="His client" className="max-lg:col-span-2">
          <div className="flex gap-1">
            {(['buy', 'sell'] as const).map((v) => (
              <button key={v} type="button" disabled={disabled} onClick={() => pickSide(v)}
                className={`seg flex-1 min-h-[44px] lg:min-h-[40px] disabled:opacity-60 ${side === v ? 'seg-on' : ''}`}>
                {v === 'buy' ? 'Buys' : 'Sells'} <b className="font-semibold">{coinWord}</b>
              </button>
            ))}
          </div>
        </Field>
        <Field label="Coin">
          <select className="select w-full" value={coin} onChange={(e) => { if (!locked && !busy) pickCoin(e.target.value); }} disabled={disabled} aria-label="Coin">
            {COINS.map((a) => <option key={a} value={a}>{displayAsset(a)}</option>)}
          </select>
        </Field>
        <Field label="Currency">
          <select className="select w-full" value={fiat} onChange={(e) => { if (!locked && !busy) pickFiat(e.target.value); }} disabled={disabled} aria-label="Currency">
            {FIATS.map((a) => <option key={a} value={a}>{displayAsset(a)}</option>)}
          </select>
        </Field>
        <Field label="Desk rate" hint={desk.rateIsDerived ? `${desk.rateWords} · auto` : desk.rateWords} className="max-lg:col-span-2">
          <input className={`input${desk.rateIsDerived ? ' text-tg-hint' : ''}`} value={desk.rate} onChange={(e) => desk.setRate(e.target.value)} inputMode="decimal" disabled={disabled} aria-label="Desk rate" />
          <ErrorLine>{deskWords}</ErrorLine>
        </Field>
        {/* The desk total is the trade's posted leg: correcting it to what was paid re-derives the desk rate (12 Sept). */}
        <Field label="Desk total" hint={desk.isDerived(fiat) ? `${displayAsset(fiat)} · auto` : displayAsset(fiat)} className="max-lg:col-span-2">
          <input className={`input${desk.isDerived(fiat) ? ' text-tg-hint' : ''}`} value={shownAmount(deskTotal, fiat, desk.isDerived(fiat), editing === 'deskTotal')} onChange={(e) => desk.setAmount(fiat, e.target.value)} onFocus={() => setEditing('deskTotal')} onBlur={() => setEditing(null)} inputMode="decimal" disabled={disabled} aria-label="Desk total" />
          <ErrorLine>{deskTotalWords}</ErrorLine>
        </Field>
      </FormGroup>

      <FormGroup title="Client settlement">
        <Field className="max-[380px]:col-span-2" label="Client pays" hint={side === 'buy' ? clientTotalHint : qtyHint}>
          {side === 'buy' ? clientTotalBox : qtyBox}
          <ErrorLine>{side === 'buy' ? clientTotalWords : qtyWords}</ErrorLine>
        </Field>
        <Field className="max-[380px]:col-span-2" label="Client receives" hint={side === 'buy' ? qtyHint : clientTotalHint}>
          {side === 'buy' ? qtyBox : clientTotalBox}
          <ErrorLine>{side === 'buy' ? qtyWords : clientTotalWords}</ErrorLine>
        </Field>
        <Field label="Client rate" hint={client.rateIsDerived ? `${client.rateWords} · auto` : client.rateWords} className="max-lg:col-span-2">
          <input className={`input${client.rateIsDerived ? ' text-tg-hint' : ''}`} value={client.rate} onChange={(e) => client.setRate(e.target.value)} inputMode="decimal" disabled={disabled} aria-label="Client rate" />
          <ErrorLine>{clientWords}</ErrorLine>
        </Field>
        {/* THE MARGIN AS A RESULT, not an input: the client total less the desk total — the figures that post. Amber
            when he dealt worse than the desk rate. */}
        <Field label="Margin" hint={plan && plan.margin < 0 ? 'below desk rate' : 'on his account'} className="max-lg:col-span-2">
          <p className={`py-2 font-mono tabular text-body lg:py-2.5 ${plan && plan.margin < 0 ? 'text-warn' : plan ? 'text-tg-text' : 'text-tg-hint'}`}>
            {plan ? money(plan.margin, fiat, 2) : '—'}
          </p>
        </Field>

        {accounts === 'failed' && (
          <StateLine tone="warn" more={readWords(treasury.error)} action={retry(treasury.refetch)} className="col-span-2">
            Couldn&rsquo;t read the accounts — try again.
          </StateLine>
        )}

        <LegField
          label="Client pays into"
          checked={moneyOn} onChange={setMoneyOn}
          done={landed.money?.reference ?? null}
          disabled={busy || !!landed.money}
        >
          {accountsDown ? <AccountsDown value={moneyRail} /> : (
            <RailPicker balanceBelow
              value={moneyRail}
              onChange={side === 'buy' ? setFiatRail : pickWallet}
              rows={rows}
              partySlug={slug}
              partyEntity={entity}
              allowedKinds={kindsForCategory(side === 'buy' ? 'fiat' : 'crypto')}
              loading={treasury.isFetching || accounts === 'waiting'}
              readFailed={accounts === 'failed'}
              placeholder="Pay-in account"
            />
          )}
          <ErrorLine>
            {moneyOn && moneyMismatch
              && `That account holds ${displayAsset(moneyRail.asset)}, not ${displayAsset(plan?.moneyIn.asset ?? (side === 'buy' ? fiat : coin))} — check the account.`}
          </ErrorLine>
        </LegField>
        <LegField
          label={side === 'buy' ? 'Coins sent from' : 'Money sent from'}
          checked={deliveryOn} onChange={setDeliveryOn}
          done={landed.delivery?.reference ?? null}
          disabled={busy || !!landed.delivery}
        >
          {accountsDown ? <AccountsDown value={deliveryRail} /> : (
            <RailPicker balanceBelow
              value={deliveryRail}
              onChange={side === 'buy' ? pickWallet : setFiatRail}
              rows={rows}
              partySlug={slug}
              partyEntity={entity}
              allowedKinds={kindsForCategory(side === 'buy' ? 'crypto' : 'fiat')}
              loading={treasury.isFetching || accounts === 'waiting'}
              readFailed={accounts === 'failed'}
              placeholder="Pay-out account"
            />
          )}
          <ErrorLine>
            {deliveryOn && deliveryMismatch
              && `That account holds ${displayAsset(deliveryRail.asset)}, not ${displayAsset(plan?.moneyOut.asset ?? (side === 'buy' ? coin : fiat))} — check the account.`}
          </ErrorLine>
        </LegField>
      </FormGroup>

      {/* Optional client identity, the date and notes (Astra: hide optional identity and notes). The date rides on the
          press ("· as of 24 Oct") and in the review once set, so a backdated entry is never booked unseen. */}
      <div className="border-t border-stroke-soft pt-4">
        <MoreDetails label="More">
          <Field label="Client" hint="optional">
            <input className="input" value={clientName} onChange={(e) => setClientName(e.target.value)} disabled={disabled} aria-label="Client" />
          </Field>
          <AsOfChips label="When did this happen?" value={when} onChange={onWhenChange} disabled={disabled} />
          <Field label="Notes">
            <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" disabled={disabled} />
          </Field>
        </MoreDetails>
      </div>
    </FormCard>
  );

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): the three entries as POSTED —
     the trade's id to copy, the margin, the party's box now; a movement left for his page, or a trade the books already
     held, says so on one line. */
  const receipt = submit.isSuccess && posted ? (
    <Receipt
      title="Trade booked"
      reference={posted.dealId}
      facts={[
        { label: 'Party', value: posted.name },
        { label: 'Margin', value: <ReceiptAmount value={posted.margin} asset={posted.fiat} /> },
        // Rows, not two sentences (1 Nov 2026 audit #6): the words at the left, each figure at the right with its unit.
        !unknown && { label: 'Balance now', value: <BalanceRows className="!mt-0 min-w-[14rem]" lines={[{ asset: posted.fiat, now: fiatAfter }, { asset: posted.coin, now: coinAfter }]} /> },
        { label: 'As of', value: postedAsOf(posted.asOf, fmtDay, submit.submittedAt) },
      ]}
      extra={(posted.tradeDeduped || !posted.money || !posted.delivery) && (
        <StateLine tone="warn">
          {posted.tradeDeduped ? 'Trade was already booked — its first date stands.'
            : `${!posted.money ? (posted.side === 'buy' ? 'Money in' : 'Coins in') : (posted.side === 'buy' ? 'Coins out' : 'Money out')} not recorded — add it from his page.`}
        </StateLine>
      )}
      another={{ label: 'Book another', onClick: () => submit.reset() }}
      links={<Link to={partyHref} className={TAP}>Party page →</Link>}
    />
  ) : null;

  // About this job: the explanation, the desk and when the books were read — beside the card, and under it on the
  // phone (Astra, 1 Nov 2026).
  const help = (
    <>
      <p>
        Three ordinary entries in one press: the trade with {name} at the desk rate, settle later; the
        client&rsquo;s money in at the client rate; and the delivery out. The sweep closes the trade from the two
        movements.
      </p>
      <p>
        The margin — the client total less the desk total — stays on his account, settled with him separately,
        never the desk&rsquo;s income.
        {rides
          ? <> {name} carries a running account, so the trade closes itself and the margin rides on his balance.</>
          : <> {name} does not carry a running account: the trade closes once the movements cover it in full.</>}
      </p>
      <p>
        On each side any two of quantity, rate and total fill the third; correct a total to what was paid and its
        rate follows. Untick a movement that has not happened and record it from his page when it does.
      </p>
    </>
  );
  const facts = [{ label: 'Desk', value: entity === 'dubaiotc' ? 'Dubai' : 'India' }, { label: 'Read', value: partyRead === 'known' ? fmtTime(q.dataUpdatedAt) : '—' }];

  return (
    <>
      {head}
      <FormPage
        card={receipt ? <section className="rounded-[10px] border border-stroke bg-panel p-4 lg:p-6">{receipt}</section> : card}
        // After a posting the receipt says it all; the pre-confirmation column goes (Astra, 1 Nov 2026).
        below={receipt ? null : <AboutFold help={help} facts={facts} />}
        // The column only while there is a readback to show; before Review the explanation sits under the form.
        aside={receipt || !review ? null :
          <BeforeConfirming
            review={review}
            help={help}
            facts={facts}
          />
        }
      />
    </>
  );
}

/** One of the client's two movements: the tick (ticked by default; an unticked one is left for his page), and the
 *  account it went through. Once posted it reads as done, with its reference. */
function LegField({ label, checked, onChange, done, disabled, children }: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  done: string | null;
  disabled: boolean;
  children: ReactNode;
}) {
  return (
    // Whole line on the phone: an account's name and its balance figure do not fit half of 390 px.
    <div className="min-w-0 space-y-1 max-lg:col-span-2">
      <label className="flex min-h-[24px] cursor-pointer items-center gap-2 text-meta text-tg-hint">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span className="min-w-0 truncate">{label}{done && <span className="text-pos"> · posted</span>}</span>
      </label>
      {done ? <p className="truncate py-2 font-mono text-meta">{done}</p>
        : checked ? <div>{children}</div>
        : <p className="py-2 text-meta text-tg-hint">Later, from his page</p>}
    </div>
  );
}
